import { type Ref, computed, onMounted, ref, watch } from 'vue'

import type { MediaVideoProps } from '@fondof-ui/components/media/types'

export const useVideo = (
  props: MediaVideoProps,
  videoRef: Ref<HTMLVideoElement | null>
) => {
  const isVideoPlaying = ref(false)

  onMounted(() => {
    if (videoRef.value) {
      initVideoPreload(videoRef.value)
    }
  })

  watch(
    () => videoRef.value?.ended,
    (ended) => {
      if (ended && videoRef.value?.autoplay) {
        resetVideoState()
      }
    }
  )

  const hasDataAttr = computed(() => {
    const hasMobileDataAttr = videoRef.value?.hasAttribute(
      'data-src-video-mobile'
    )
    const hasTabletDataAttr = videoRef.value?.hasAttribute(
      'data-src-video-tablet'
    )
    const hasDesktopDataAttr = videoRef.value?.hasAttribute(
      'data-src-video-desktop'
    )

    return hasMobileDataAttr || hasTabletDataAttr || hasDesktopDataAttr
  })

  const videoSrc = computed(() => {
    const sources = [
      { minWidth: 993, attr: 'data-src-video-desktop' },
      { minWidth: 577, attr: 'data-src-video-tablet' },
      { minWidth: 0, attr: 'data-src-video-mobile' },
    ]

    if (!hasDataAttr.value) {
      return props.videoDesktop?.url
    }

    const width = window.innerWidth
    const source = sources.find(
      (source) =>
        width >= source.minWidth && videoRef.value?.getAttribute(source.attr)
    )

    return (
      (source && videoRef.value?.getAttribute(source.attr)) ||
      props.videoDesktop?.url ||
      props.videoTablet?.url ||
      props.videoMobile?.url ||
      ''
    )
  })

  const resetVideoState = () => {
    if (!videoRef.value) return
    videoRef.value.currentTime = 0
    isVideoPlaying.value = false
  }

  const pauseAllVideos = () => {
    document.querySelectorAll('video').forEach((video) => {
      if (!video.closest('.hero')) {
        video.pause()
      }
    })
  }

  const pauseAllVideosAndPlayCurrent = () => {
    pauseAllVideos()
    videoRef.value?.play()
  }

  const playOrPause = () => {
    if (!isVideoPlaying.value) {
      pauseAllVideosAndPlayCurrent()
      isVideoPlaying.value = true
      return
    }
    videoRef.value?.pause()
    isVideoPlaying.value = false
  }

  const hasSelfHostedVideo = computed(
    () =>
      props.videoDesktop?.url ||
      props.videoMobile?.url ||
      props.videoTablet?.url
  )

  const hasImage = computed(
    () =>
      props.imageMobile?.url ||
      props.imageTablet?.url ||
      props.imageDesktop?.url
  )

  const videoIdUrl = computed(() => {
    if (!props.videoId) return ''

    if (props.videoIdType === 'youtube') {
      return `https://www.youtube.com/embed/${props.videoId}?enablejsapi=1`
    }

    return `https://vimeo.com/${props.videoId}`
  })

  const videoOptions = computed(() => {
    let options = 'disablePictureInPicture playsinline'
    if (props.videoAutoplay) {
      options += ' autoplay'
    }
    if (props.videoMuted || props.videoAutoplay) {
      options += ' muted'
    }
    if (props.videoLoop) {
      options += ' loop'
    }
    if (props.videoControls) {
      options += ' controls'
    }
    return options
  })

  const videoPreload = computed(() => {
    if (props.videoPreload) {
      return props.videoPreload
    }
    return 'none'
  })

  const videoSourceAttributes = computed(() => {
    const sources = [
      { key: 'desktop', data: props.videoDesktop?.url },
      { key: 'mobile', data: props.videoMobile?.url },
      { key: 'tablet', data: props.videoTablet?.url },
    ].filter((source) => Boolean(source.data))

    return sources.reduce((acc: Record<string, string>, { key, data }) => {
      acc.src = `${data}#t=0.001`
      acc[`data-src-video-${key}`] = `${data}#t=0.001`
      return acc
    }, {})
  })

  const videoAttributes = computed(() => {
    const attrs: Record<string, string> = {
      ...videoOptions.value
        .split(' ')
        .reduce((acc: Record<string, string>, attr) => {
          acc[attr] = ''
          return acc
        }, {}),
    }
    return { ...attrs, ...videoSourceAttributes.value }
  })

  const showPreviewImage = computed(() => {
    return (
      hasImage.value &&
      !props.videoAutoplay &&
      !isVideoPlaying.value &&
      !videoRef.value?.played.length
    )
  })

  const showPlayIcon = computed(() => {
    return (
      hasSelfHostedVideo.value &&
      !props.videoAutoplay &&
      props.showPlayButton &&
      !isVideoPlaying.value
    )
  })

  const setVideoPreloadAttribute = (element: HTMLElement) => {
    element.setAttribute('preload', 'metadata')
  }

  const initVideoPreload = (selector: HTMLElement) => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const videoElements = entries[0].target.querySelectorAll('video')
          if (videoElements) {
            videoElements.forEach((video) => setVideoPreloadAttribute(video))
          }
        }
      },
      {
        rootMargin: '50% 0px',
        threshold: 0,
      }
    )
    observer.observe(selector)
    return (selector: HTMLElement) => {
      observer.unobserve(selector)
    }
  }

  return {
    videoSrc,
    isVideoPlaying,
    resetVideoState,
    pauseAllVideos,
    pauseAllVideosAndPlayCurrent,
    playOrPause,
    hasSelfHostedVideo,
    hasImage,
    videoIdUrl,
    videoOptions,
    videoPreload,
    videoAttributes,
    showPreviewImage,
    showPlayIcon,
    setVideoPreloadAttribute,
    initVideoPreload,
  }
}
